// Every content URL is language-prefixed: /{lang}/<suffix> (ADR-0003). The language
// is handled centrally in matchRoute/buildPath — route-table entries match and build
// the language-free suffix, and `lang` is attached to every route. This keeps the
// chosen language in the URL for ALL routes (login, stats, moderation, …) so it
// survives reloads and direct links, not just /{lang}/pages/... reads.
export type Route =
  | { kind: 'reader'; slug: string | null; lang?: string }
  | { kind: 'allPages'; lang?: string }
  | { kind: 'all'; prefix?: string; lang?: string }
  | { kind: 'categories'; lang?: string }
  | { kind: 'category'; name: string; lang?: string }
  | { kind: 'recentChanges'; lang?: string }
  | { kind: 'reports'; lang?: string }
  | { kind: 'login'; lang?: string }
  | { kind: 'register'; lang?: string }
  | { kind: 'changePassword'; lang?: string }
  | { kind: 'map'; lang?: string }
  | {
      kind: 'newProposal';
      slug?: string;
      title?: string;
      language?: string;
      translationGroup?: string;
      lang?: string;
    }
  | { kind: 'editProposal'; slug: string; lang?: string }
  | { kind: 'history'; slug: string; revisionID?: string; lang?: string }
  | { kind: 'pageInfo'; slug: string; lang?: string }
  | { kind: 'moderation'; proposalID: string | null; lang?: string }
  | { kind: 'auditLog'; lang?: string }
  | { kind: 'stats'; lang?: string }
  | { kind: 'watchedPages'; lang?: string }
  | { kind: 'adminUsers'; lang?: string };

// English is the base content language (ADR-0003): an unprefixed URL resolves to /en/.
export const defaultLang = 'en';

// A leading two-letter segment is treated as the content language.
const langPrefixPattern = /^\/([a-z]{2})(?=\/|$)/;

type RouteTableEntry = {
  kind: Route['kind'];
  // match receives the path with the /{lang}/ prefix already stripped.
  match(suffix: string): Route | null;
  // build returns the language-free suffix (matchRoute/buildPath add the prefix).
  build(route: Route): string | null;
};

export const routeTable: RouteTableEntry[] = [
  {
    kind: 'login',
    match: (p) => (p === '/login' ? { kind: 'login' } : null),
    build: (r) => (r.kind === 'login' ? '/login' : null),
  },
  {
    kind: 'register',
    match: (p) => (p === '/register' ? { kind: 'register' } : null),
    build: (r) => (r.kind === 'register' ? '/register' : null),
  },
  {
    kind: 'changePassword',
    match: (p) => (p === '/account/password' ? { kind: 'changePassword' } : null),
    build: (r) => (r.kind === 'changePassword' ? '/account/password' : null),
  },
  {
    kind: 'allPages',
    match: (p) => (p === '/pages' ? { kind: 'allPages' } : null),
    build: (r) => (r.kind === 'allPages' ? '/pages' : null),
  },
  {
    kind: 'all',
    match: (p) => (p === '/all' ? { kind: 'all' } : null),
    build: (r) => {
      if (r.kind !== 'all') return null;
      return r.prefix ? `/all?prefix=${encodeURIComponent(r.prefix)}` : '/all';
    },
  },
  {
    kind: 'categories',
    match: (p) => (p === '/categories' ? { kind: 'categories' } : null),
    build: (r) => (r.kind === 'categories' ? '/categories' : null),
  },
  {
    kind: 'category',
    match: (p) => {
      const m = p.match(/^\/categories\/([^/]+)$/);
      return m?.[1] != null ? { kind: 'category', name: decodeURIComponent(m[1]) } : null;
    },
    build: (r) => (r.kind === 'category' ? `/categories/${encodeURIComponent(r.name)}` : null),
  },
  {
    kind: 'recentChanges',
    match: (p) => (p === '/recent' ? { kind: 'recentChanges' } : null),
    build: (r) => (r.kind === 'recentChanges' ? '/recent' : null),
  },
  {
    kind: 'reports',
    match: (p) => (p === '/reports' ? { kind: 'reports' } : null),
    build: (r) => (r.kind === 'reports' ? '/reports' : null),
  },
  {
    kind: 'stats',
    match: (p) => (p === '/stats' ? { kind: 'stats' } : null),
    build: (r) => (r.kind === 'stats' ? '/stats' : null),
  },
  {
    kind: 'watchedPages',
    match: (p) => (p === '/watchlist' ? { kind: 'watchedPages' } : null),
    build: (r) => (r.kind === 'watchedPages' ? '/watchlist' : null),
  },
  {
    kind: 'adminUsers',
    match: (p) => (p === '/admin/users' ? { kind: 'adminUsers' } : null),
    build: (r) => (r.kind === 'adminUsers' ? '/admin/users' : null),
  },
  {
    kind: 'auditLog',
    match: (p) => (p === '/moderation/audit' ? { kind: 'auditLog' } : null),
    build: (r) => (r.kind === 'auditLog' ? '/moderation/audit' : null),
  },
  {
    kind: 'map',
    match: (p) => (p === '/map' ? { kind: 'map' } : null),
    build: (r) => (r.kind === 'map' ? '/map' : null),
  },
  {
    kind: 'newProposal',
    match: (p) => (p === '/propose' ? { kind: 'newProposal' } : null),
    build: (r) => {
      if (r.kind !== 'newProposal') return null;
      const params = new URLSearchParams();
      if (r.slug) params.set('slug', r.slug);
      if (r.title) params.set('title', r.title);
      const qs = params.toString();
      return qs ? `/propose?${qs}` : '/propose';
    },
  },
  {
    kind: 'moderation',
    match: (p) => {
      if (p === '/moderation') return { kind: 'moderation', proposalID: null };
      const m = p.match(/^\/moderation\/proposals\/([^/]+)$/);
      return m?.[1] != null ? { kind: 'moderation', proposalID: decodeSlug(m[1]) } : null;
    },
    build: (r) => {
      if (r.kind !== 'moderation') return null;
      return r.proposalID
        ? `/moderation/proposals/${encodeURIComponent(r.proposalID)}`
        : '/moderation';
    },
  },
  {
    kind: 'editProposal',
    match: (p) => {
      const m = p.match(/^\/pages\/([^/]+)\/edit$/);
      return m?.[1] != null ? { kind: 'editProposal', slug: decodeSlug(m[1]) } : null;
    },
    build: (r) => (r.kind === 'editProposal' ? `/pages/${encodeURIComponent(r.slug)}/edit` : null),
  },
  {
    kind: 'pageInfo',
    match: (p) => {
      const m = p.match(/^\/pages\/([^/]+)\/info$/);
      return m?.[1] != null ? { kind: 'pageInfo', slug: decodeSlug(m[1]) } : null;
    },
    build: (r) => (r.kind === 'pageInfo' ? `/pages/${encodeURIComponent(r.slug)}/info` : null),
  },
  {
    kind: 'history',
    match: (p) => {
      const revM = p.match(/^\/pages\/([^/]+)\/history\/([^/]+)$/);
      if (revM?.[1] != null && revM?.[2] != null) {
        return { kind: 'history', slug: decodeSlug(revM[1]), revisionID: decodeSlug(revM[2]) };
      }
      const histM = p.match(/^\/pages\/([^/]+)\/history$/);
      return histM?.[1] != null ? { kind: 'history', slug: decodeSlug(histM[1]) } : null;
    },
    build: (r) => {
      if (r.kind !== 'history') return null;
      const base = `/pages/${encodeURIComponent(r.slug)}/history`;
      return r.revisionID ? `${base}/${encodeURIComponent(r.revisionID)}` : base;
    },
  },
  {
    kind: 'reader',
    match: (p) => {
      const pageM = p.match(/^\/pages\/([^/]+)$/);
      if (pageM?.[1] != null) {
        return { kind: 'reader', slug: decodeSlug(pageM[1]) };
      }
      return p === '/' ? { kind: 'reader', slug: null } : null;
    },
    build: (r) => {
      if (r.kind !== 'reader') return null;
      return r.slug === null ? '/' : `/pages/${encodeURIComponent(r.slug)}`;
    },
  },
];

export function matchRoute(pathname: string): Route {
  let lang = defaultLang;
  let suffix = pathname;

  const prefixMatch = pathname.match(langPrefixPattern);
  if (prefixMatch) {
    lang = prefixMatch[1];
    suffix = pathname.slice(prefixMatch[0].length) || '/';
  }

  for (const entry of routeTable) {
    const result = entry.match(suffix);
    if (result !== null) {
      return { ...result, lang };
    }
  }

  return { kind: 'reader', slug: null, lang };
}

export function buildPath(route: Route, homeSlug?: string, contentLang?: string): string {
  const lang = contentLang ?? route.lang ?? defaultLang;

  if (
    route.kind === 'reader' &&
    (route.slug === null || (homeSlug != null && route.slug === homeSlug))
  ) {
    return `/${lang}/`;
  }

  for (const entry of routeTable) {
    const suffix = entry.build(route);
    if (suffix !== null) {
      return `/${lang}${suffix}`;
    }
  }

  return `/${lang}/`;
}

function decodeSlug(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
